import {
  CustomAmountsFormData,
  amountKeys,
} from '../../../types/givingCheckoutForm';
import React, { FunctionComponent, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';

import BlockRadioList from '../../components/BlockRadioList/BlockRadioList';
import CheckoutPreview from '../../components/CheckoutPreview/CheckoutPreview';
import { PAGES } from '../../constants/pages';
import Steps from '../../components/Steps/Steps';
import SuggestedAmountInput from '../../components/SuggestedAmountInput/SuggestedAmountInput';
import { suggestedAmountDefaults } from '../../components/FormContext/FormContext';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import { useHeadingSetFocusRef } from '../../hooks/useHeadingSetFocusRef';
import { useTranslation } from 'react-i18next';

const CustomAmounts: FunctionComponent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const formMethods = useFormContext<CustomAmountsFormData>();
  const { handleSubmit, setError, clearErrors, setValue } = formMethods;
  const headingRef = useHeadingSetFocusRef();

  const [
    numberOfCustomAmountsLive,
    suggestedAmountDefaultLive,
    customAmountsEnabledLive,
  ] = useWatch<
    CustomAmountsFormData,
    ['numberOfCustomAmounts', 'suggestedAmountDefault', 'customAmountsEnabled']
  >({
    name: [
      'numberOfCustomAmounts',
      'suggestedAmountDefault',
      'customAmountsEnabled',
    ],
  });

  const checkForDuplicatesSuggestedAmounts = (
    array: string[],
    input: string,
    match?: string,
  ) => {
    if (!match) return false;
    if (array.filter((value) => value === match).length > 1) {
      setError(input as keyof CustomAmountsFormData, {
        message: t('suggestedAmountError.duplicate'),
      });
      return true;
    }
    clearErrors(input as keyof CustomAmountsFormData);
    return false;
  };

  const onSubmit = handleSubmit(async (data) => {
    if (data.customAmountsEnabled === 'yes') {
      const submittedAmountsValues = Object.values(
        data.suggestedAmounts ?? [],
      ).slice(0, Number(data.numberOfCustomAmounts));
      const submittedAmounts = Array.from(new Set(submittedAmountsValues));
      if (submittedAmounts.length !== Number(data.numberOfCustomAmounts)) {
        for (let i = 1; i <= 5; i++) {
          checkForDuplicatesSuggestedAmounts(
            submittedAmountsValues,
            `suggestedAmounts.amount${i}`,
            data.suggestedAmounts[`amount${i}` as amountKeys],
          );
        }
        return;
      }
    }

    navigate(`${PAGES.Descriptions}${location.search}`);
  });

  const suggestedAmountInputs = useMemo(
    () =>
      new Array(Number(numberOfCustomAmountsLive ?? 5))
        .fill('')
        .map((id, i) => (
          <SuggestedAmountInput key={`suggested-amount-${i}`} id={`${i + 1}`} />
        )),
    [numberOfCustomAmountsLive],
  );

  useDocumentTitle(t('customAmounts.heading'));

  return (
    <div className="giving-checkout-page">
      <div className="giving-checkout-page-form">
        <form noValidate method="post" onSubmit={onSubmit}>
          <Steps start={2} end={5} />
          <h1 className="jg-h3" ref={headingRef} tabIndex={-1}>
            {t('customAmounts.heading')}
          </h1>
          <p className="jg-space-mblg jg-font-weight-regular jg-color--empress">
            {t('customAmounts.description')}
          </p>

          <p className="jg-h6">{t('customAmounts.question')}</p>
          <BlockRadioList
            name="customAmountsEnabled"
            items={[
              {
                title: t('common.no'),
                value: 'no',
                onChange: () =>
                  setValue(
                    'numberOfCustomAmounts',
                    suggestedAmountDefaults.numberOfCustomAmounts,
                  ),
              },
              {
                title: t('common.yes'),
                value: 'yes',
              },
            ]}
          />

          {customAmountsEnabledLive === 'yes' && (
            <>
              <p className="jg-h6">{t('customAmounts.label')}</p>
              <BlockRadioList
                name="numberOfCustomAmounts"
                items={[
                  {
                    title: '5',
                    value: '5',
                  },
                  {
                    title: '4',
                    value: '4',
                  },
                  {
                    title: '3',
                    value: '3',
                  },
                ]}
              />

              {suggestedAmountInputs}
            </>
          )}

          <button
            className="jg-btn jg-space-mtlg"
            type="submit"
            data-qa="customamounts-continue"
          >
            {t('common.continue')}
          </button>
          <button
            className="giving-checkout-button-link"
            type="button"
            data-track="custom-amounts-continue"
            onClick={() => navigate(`${PAGES.Frequency}${location.search}`)}
          >
            {t('common.back')}
          </button>
        </form>
      </div>
      <div className="giving-checkout-page-preview">
        <CheckoutPreview
          suggestedAmountDefault={suggestedAmountDefaultLive}
          previewBlock={['frequency', 'description', 'cta']}
        />
      </div>
    </div>
  );
};

export default CustomAmounts;
